import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { EndPoints, OrderStatus } from '../../common/enums/enums';
import { errorResponseHelper } from '../../helpers/apiResponseHelpers';
import { editVehicle } from '../../helpers/editVehicle';
import { amplifyApi } from '../../hooks/amplifyApi';
import orderDetailsTypes from '../types/orderDetailsTypes';

export const openEditVechileModal = createAction(
  orderDetailsTypes.openEditVehicleModal
);

export const openDeclineConfirm = createAction(
  orderDetailsTypes.openDeclineConfirm
);
export const closeDeclineConfirm = createAction(
  orderDetailsTypes.closeDeclineConfirm
);

export const resetSurveySentStatus = createAction(
  orderDetailsTypes.resetSurveySentStatus
);

export const openModalQualitySurvey = createAction(
  orderDetailsTypes.openModalQualitySurvey
);

export const closeModalQualitySurvey = createAction(
  orderDetailsTypes.closeModalQualitySurvey
);

export const hideCancelConfirmationSnackBar = createAction(
  orderDetailsTypes.hideCancelConfirmationSnackBar
);

export const openAddServiceModal = createAction(
  orderDetailsTypes.openAddServiceModal
);

export const cloaseAddServiceModal = createAction(
  orderDetailsTypes.closeAddServiceModal
);

export const closeDeleteConfirmationModal = createAction(
  orderDetailsTypes.closeDeleteConfirmationModal
);

export const closeEditVechileModal = createAction(
  orderDetailsTypes.closeEditVehicleModal
);

export const clearCompletionEditStatus = createAction(
  orderDetailsTypes.clearPromiseDateStatus
);

export const clearAddServiceError = createAction(
  orderDetailsTypes.clearAddServiceError
);

export const getWorkOrderData = createAsyncThunk(
  orderDetailsTypes.getWorkOrderData,
  async ({ vendorOrderId }, { rejectWithValue }) => {
    try {
      const response = await amplifyApi(
        null,
        EndPoints.ORDERS.GET_DATA.ENDPOINT.replace(
          '{vendorOrderId}',
          vendorOrderId
        ),
        EndPoints.ORDERS.GET_DATA.METHOD,
        true
      );
      return { status: response.status, data: response.data };
    } catch (error) {
      return rejectWithValue({ status: error.response.status });
    }
  }
);

export const editVehicleDetails = createAsyncThunk(
  orderDetailsTypes.editVehicleDetails,
  async ({ vendorOrderId, newData, oldData }) => {
    await editVehicle(newData, oldData);

    return await getOrderDetails(vendorOrderId);
  }
);

async function getOrderDetails(vendorOrderId) {
  return await amplifyApi(
    null,
    EndPoints.ORDERS.GET_DATA.ENDPOINT.replace(
      '{vendorOrderId}',
      vendorOrderId
    ),
    EndPoints.ORDERS.GET_DATA.METHOD
  );
}

export const deleteServiceFromWorkOrder = createAsyncThunk(
  orderDetailsTypes.deleteService,
  async ({ vendorOrderId, serviceId }, { rejectWithValue }) => {
    try {
      const response = await amplifyApi(
        null,
        EndPoints.ORDERS.DELETE_SERVICE.ENDPOINT.replace(
          '{vendorOrderId}',
          vendorOrderId
        ).replace('{serviceId}', serviceId),
        EndPoints.ORDERS.DELETE_SERVICE.METHOD,
        true
      );
      return { status: response.status, data: response.data };
    } catch (error) {
      return rejectWithValue(errorResponseHelper(error));
    }
  }
);

export const getPrices = createAsyncThunk(
  orderDetailsTypes.getServices,
  async ({ vendor_id, departmentId }) => {
    return await amplifyApi(
      null,
      EndPoints.ORDERS.NEW_ORDER.GET_SERVICES_BY_DEPARTMENT.ENDPOINT.replace(
        '{vendorId}',
        vendor_id
      ).replace('{departmentId}', departmentId),
      EndPoints.ORDERS.NEW_ORDER.GET_SERVICES_BY_DEPARTMENT.METHOD
    );
  }
);

export const addServiceToWorkOrder = createAsyncThunk(
  orderDetailsTypes.addServiceToWorkOrder,
  async ({ vendorOrderId, serviceId }, { rejectWithValue }) => {
    try {
      const response = await amplifyApi(
        null,
        EndPoints.ORDERS.ADD_SERVICE.ENDPOINT.replace(
          '{vendorOrderId}',
          vendorOrderId
        ).replace('{serviceId}', serviceId),
        EndPoints.ORDERS.ADD_SERVICE.METHOD,
        true
      );

      return response.data;
    } catch (error) {
      const woDetails = error.response.data.data;
      return rejectWithValue({
        ...errorResponseHelper(error),
        data: { ...woDetails },
      });
    }
  }
);

export const getWorkOrderToPdf = createAsyncThunk(
  orderDetailsTypes.getWorkOrderToPdf,
  async ({ vendor_order_id }) => {
    return await amplifyApi(
      null,
      EndPoints.ORDERS.EXPORT_WORK_ORDER.ENDPOINT.replace(
        '{vendor_order_id}',
        vendor_order_id
      ),
      EndPoints.ORDERS.EXPORT_WORK_ORDER.TYPE
    );
  }
);

export const cancelStepChangeStatus = createAsyncThunk(
  orderDetailsTypes.cancelStepStatusChange,
  async ({ VoId, cancellingNote: notes }, { rejectWithValue }) => {
    try {
      const response = await amplifyApi(
        { notes },
        EndPoints.ORDERS.CANCEL_ORDER.ENDPOINT.replace(
          '{vendor_order_id}',
          VoId
        ),
        EndPoints.ORDERS.CANCEL_ORDER.TYPE,
        true
      );
      return { status: response.status, data: response.data };
    } catch (error) {
      return rejectWithValue(errorResponseHelper(error));
    }
  }
);

export const setPromiseDate = createAsyncThunk(
  orderDetailsTypes.setPromiseDate,
  async ({ vendorOrderId, promiseDate }, { rejectWithValue }) => {
    return await amplifyApi(
      { promise_datetime: promiseDate },
      EndPoints.ORDERS.EDIT_VENDOR_ORDER.ENDPOINT.replace(
        '{vendorOrderId}',
        vendorOrderId
      ),
      EndPoints.ORDERS.EDIT_VENDOR_ORDER.METHOD
    );
  }
);

export const addSurvey = createAsyncThunk(
  orderDetailsTypes.addSurvey,
  async ({ vendorOrderId, survey }) => {
    return await amplifyApi(
      survey,
      EndPoints.ORDERS.ADD_SURVEY.ENDPOINT.replace(
        '{vendorOrderId}',
        vendorOrderId
      ),
      EndPoints.ORDERS.ADD_SURVEY.METHOD
    );
  }
);

export const addWorkOrderNote = createAsyncThunk(
  orderDetailsTypes.addWorkOrderNote,
  async ({ VoId, note }) => {
    return await amplifyApi(
      { observation: note },
      EndPoints.ORDERS.EDIT_VENDOR_ORDER.ENDPOINT.replace(
        '{vendorOrderId}',
        VoId
      ),
      EndPoints.ORDERS.EDIT_VENDOR_ORDER.METHOD,
      true
    );
  }
);

export const approveOrder = createAsyncThunk(
  orderDetailsTypes.approveOrder,
  async order => {
    return await amplifyApi(
      {
        status: OrderStatus.approved,
      },
      `${EndPoints.ORDERS.UNAPPROVED.APPROVE_DECLINE_ORDER.ENDPOINT}${order.vendorOrderId}`,
      EndPoints.ORDERS.UNAPPROVED.APPROVE_DECLINE_ORDER.METHOD
    );
  }
);

export const declineOrder = createAsyncThunk(
  orderDetailsTypes.declineOrder,
  async ({ order, observation }) => {
    return await amplifyApi(
      {
        status: OrderStatus.declined,
        observation,
      },
      `${EndPoints.ORDERS.UNAPPROVED.APPROVE_DECLINE_ORDER.ENDPOINT}${order.vendorOrderId}`,
      EndPoints.ORDERS.UNAPPROVED.APPROVE_DECLINE_ORDER.METHOD
    );
  }
);
